(function () {
  'use strict';

  // El script puede incluirse desde mas de un lugar (gallery block y el shell
  // de product-recommendations): inicializar una sola vez para no duplicar el
  // listener delegado de click (doble add-to-cart).
  if (window.__pchInitialized) return;
  window.__pchInitialized = true;

  const isTouch = () => window.matchMedia('(hover: none)').matches;

  // Bottom-sheet compartido para el flujo mobile: en touch no hay hover, asi
  // que "Comprar ahora" abre este preview del producto (imagen, titulo,
  // precio, opciones) y se compra desde aqui. Se construye una sola vez.
  let sheetEl = null;
  let sheetState = null;

  /**
   * Custom element that manages variant selection state inside the hover overlay.
   * The permanent card-level "Comprar ahora" button redirects to checkout, while
   * the overlay action adds the selected variant to cart without leaving the page.
   */
  class ProductCardHoverOverlay extends HTMLElement {
    connectedCallback() {
      this._selectedOptions = {};
      this._variants = [];
      this._optionNames = [];

      const variantsScript = this.querySelector('.pch-variants-json');
      if (variantsScript) {
        try {
          this._variants = JSON.parse(variantsScript.textContent);
        } catch (_) { }
      }

      const optionsScript = this.querySelector('.pch-options-json');
      if (optionsScript) {
        try {
          this._optionNames = JSON.parse(optionsScript.textContent);
        } catch (_) { }
      }

      this.querySelectorAll('.pch-size-btn, .pch-color-btn').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._selectOption(btn.dataset.optionName, btn.dataset.optionValue, btn);
        });
      });

      this.querySelectorAll('.pch-btn--view, .pch-btn--cart').forEach((action) => {
        action.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          if (action.matches('.pch-btn--cart')) {
            this.handleCartAdd(action);
          }
        });
      });

      this._selectSingleAvailableOptions();
    }

    _selectSingleAvailableOptions() {
      this._optionNames.forEach((name) => {
        const buttons = Array.from(this.querySelectorAll(`[data-option-name="${CSS.escape(name)}"]`));
        const availableButtons = buttons.filter((btn) => !btn.disabled && btn.getAttribute('aria-disabled') !== 'true');

        if (availableButtons.length === 1) {
          const btn = availableButtons[0];
          this._selectOption(btn.dataset.optionName, btn.dataset.optionValue, btn);
        }
      });
    }

    _selectOption(name, value, btn) {
      if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;

      this.querySelectorAll(`[data-option-name="${CSS.escape(name)}"]`).forEach((b) => {
        b.setAttribute('aria-pressed', 'false');
        b.classList.remove('pch-selected');
      });
      btn.setAttribute('aria-pressed', 'true');
      btn.classList.add('pch-selected');
      this._selectedOptions[name] = value;
      this._clearError();
    }

    _getSelectedVariant() {
      return this._variants.find((v) =>
        this._optionNames.every((name, i) => {
          const selected = this._selectedOptions[name];
          if (!selected) return false;
          return v[`option${i + 1}`] === selected;
        })
      );
    }

    _getMissingOptions() {
      return this._optionNames.filter((name) => !this._selectedOptions[name]);
    }

    _getActionVariant(triggerBtn) {
      const missing = this._getMissingOptions();

      if (missing.length > 0) {
        this._showError('Selecciona: ' + missing.join(', '));
        this._shakeBtn(triggerBtn);
        this._shakeCard();
        this._forceOpen();
        return null;
      }

      const variant = this._getSelectedVariant();
      if (!variant) {
        this._showError('Combinacion no disponible');
        this._shakeBtn(triggerBtn);
        this._shakeCard();
        this._forceOpen();
        return null;
      }

      if (!variant.available) {
        this._showError('Esta opcion no esta disponible');
        this._shakeBtn(triggerBtn);
        this._shakeCard();
        this._forceOpen();
        return null;
      }

      return variant;
    }

    _getCartAddUrl() {
      const routesRoot = window.Shopify?.routes?.root || '/';
      return window.Theme?.routes?.cart_add_url || `${routesRoot.endsWith('/') ? routesRoot : `${routesRoot}/`}cart/add.js`;
    }

    /**
     * Public method called by the permanent card-level "Comprar ahora" button.
     * @param {HTMLButtonElement} triggerBtn - The button element that triggered the action.
     */
    handleBuyNow(triggerBtn) {
      const variant = this._getActionVariant(triggerBtn);
      if (!variant) return;

      triggerBtn.disabled = true;

      fetch(this._getCartAddUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variant.id, quantity: 1 }),
      })
        .then((resp) => {
          if (!resp.ok) throw new Error();
          window.location.href = '/checkout';
        })
        .catch(() => {
          triggerBtn.disabled = false;
          this._showError('Error al procesar. Intenta de nuevo.');
        });
    }

    async handleCartAdd(triggerBtn) {
      const variant = this._getActionVariant(triggerBtn);
      if (!variant) return;

      const originalHtml = triggerBtn.innerHTML;
      triggerBtn.disabled = true;

      try {
        const response = await fetch(this._getCartAddUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variant.id, quantity: 1 }),
        });

        if (!response.ok) throw new Error();

        const cartResponse = await fetch('/cart.js', { credentials: 'same-origin' });
        const cart = cartResponse.ok ? await cartResponse.json() : null;

        triggerBtn.innerHTML = '<span>Agregado</span><span class="pch-btn__plus" aria-hidden="true">&#10003;</span>';

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: 'product-card-hover',
              data: {
                source: 'product-card-hover',
                itemCount: cart?.item_count || 0,
                variantId: String(variant.id),
              },
            },
          })
        );

        this._closeForced();

        setTimeout(() => {
          triggerBtn.innerHTML = originalHtml;
          triggerBtn.disabled = false;
        }, 1200);
      } catch (_) {
        triggerBtn.innerHTML = '<span>No se pudo agregar</span>';
        this._showError('Error al agregar. Intenta de nuevo.');
        setTimeout(() => {
          triggerBtn.innerHTML = originalHtml;
          triggerBtn.disabled = false;
        }, 1800);
      }
    }

    _errorTargets() {
      const targets = [this.querySelector('.pch-error')];
      const card = this.closest('product-card');
      if (card) targets.push(card.querySelector('.pch-buy-bar__error'));
      if (sheetEl && !sheetEl.hidden && sheetState && sheetState.hover === this) {
        targets.push(sheetEl.querySelector('.pch-sheet__error'));
      }
      return targets.filter(Boolean);
    }

    _showError(msg) {
      // Se escribe en el overlay Y bajo el boton Comprar ahora: el overlay va
      // oculto sin hover (y display:none en touch), asi que el aviso a nivel
      // card es el que garantiza visibilidad.
      const targets = this._errorTargets();
      if (!targets.length) return;
      targets.forEach((el) => {
        el.textContent = msg;
        el.removeAttribute('hidden');
      });
      clearTimeout(this._errorTimer);
      this._errorTimer = setTimeout(() => {
        targets.forEach((el) => el.setAttribute('hidden', ''));
      }, 3500);
    }

    _clearError() {
      this._errorTargets().forEach((el) => el.setAttribute('hidden', ''));
    }

    _shakeBtn(btn) {
      if (!btn) return;
      btn.classList.remove('pch-buy-bar__btn--error');
      void btn.offsetWidth;
      btn.classList.add('pch-buy-bar__btn--error');
      btn.addEventListener('animationend', () => btn.classList.remove('pch-buy-bar__btn--error'), { once: true });
    }

    _shakeCard() {
      const card = this.closest('product-card');
      if (!card) return;
      card.classList.remove('pch-card-shake');
      void card.offsetWidth;
      card.classList.add('pch-card-shake');
      card.addEventListener('animationend', () => card.classList.remove('pch-card-shake'), { once: true });
    }

    /**
     * Abre el selector de variantes aunque no haya hover (clave en touch,
     * donde el overlay va display:none): el visitante elige la talla ahi
     * mismo. Se cierra al tocar fuera de la card.
     */
    _forceOpen() {
      // En touch el flujo es el bottom-sheet, no el overlay forzado.
      if (isTouch()) return;
      if (this.classList.contains('pch-overlay--forced')) return;
      this.classList.add('pch-overlay--forced');

      this._outsideClose = (e) => {
        const card = this.closest('product-card');
        if (card && card.contains(e.target)) return;
        this._closeForced();
      };
      // En captura y diferido un tick para no cerrarse con el mismo tap.
      setTimeout(() => document.addEventListener('pointerdown', this._outsideClose, true), 0);
    }

    _closeForced() {
      this.classList.remove('pch-overlay--forced');
      if (this._outsideClose) {
        document.removeEventListener('pointerdown', this._outsideClose, true);
        this._outsideClose = null;
      }
    }
  }

  if (!customElements.get('product-card-hover')) {
    customElements.define('product-card-hover', ProductCardHoverOverlay);
  }

  /* ── Bottom-sheet mobile: preview del producto + selección de opciones ── */

  function ensureSheet() {
    if (sheetEl) return sheetEl;

    sheetEl = document.createElement('div');
    sheetEl.className = 'pch-sheet';
    sheetEl.hidden = true;
    sheetEl.innerHTML =
      '<div class="pch-sheet__backdrop" data-pch-sheet-close></div>' +
      '<div class="pch-sheet__panel" role="dialog" aria-modal="true" aria-label="Elegir opciones del producto">' +
      '  <button type="button" class="pch-sheet__close" data-pch-sheet-close aria-label="Cerrar">&times;</button>' +
      '  <div class="pch-sheet__head">' +
      '    <img class="pch-sheet__img" src="" alt="" width="96" height="96" loading="lazy">' +
      '    <div class="pch-sheet__meta">' +
      '      <p class="pch-sheet__title"></p>' +
      '      <p class="pch-sheet__price"></p>' +
      '      <a class="pch-sheet__link" href="#">Ver producto</a>' +
      '    </div>' +
      '  </div>' +
      '  <p class="pch-sheet__error" role="alert" hidden></p>' +
      '  <div class="pch-sheet__options"></div>' +
      '  <button type="button" class="pch-sheet__cta"><span>Comprar ahora</span></button>' +
      '</div>';
    document.body.appendChild(sheetEl);

    sheetEl.addEventListener('click', (e) => {
      if (e.target.closest('[data-pch-sheet-close]')) closeSheet();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && sheetEl && !sheetEl.hidden) closeSheet();
    });

    sheetEl.querySelector('.pch-sheet__options').addEventListener('click', (e) => {
      const btn = e.target.closest('[data-sheet-option]');
      if (!btn || btn.disabled || !sheetState) return;
      const { hover } = sheetState;
      const name = btn.dataset.optionName;
      const value = btn.dataset.optionValue;
      const orig = hover.querySelector(
        '[data-option-name="' + CSS.escape(name) + '"][data-option-value="' + CSS.escape(value) + '"]'
      );
      if (orig) hover._selectOption(name, value, orig);
      sheetEl
        .querySelectorAll('[data-sheet-option][data-option-name="' + CSS.escape(name) + '"]')
        .forEach((b) => b.classList.toggle('pch-selected', b === btn));
    });

    sheetEl.querySelector('.pch-sheet__cta').addEventListener('click', (e) => {
      if (!sheetState) return;
      sheetState.hover.handleBuyNow(e.currentTarget);
    });

    return sheetEl;
  }

  function openSheet(hover) {
    const sheet = ensureSheet();
    const card = hover.closest('product-card');
    if (!card) return;

    sheetState = { hover };

    const img = sheet.querySelector('.pch-sheet__img');
    const cardImg = card.querySelector('img');
    img.src = (cardImg && (cardImg.currentSrc || cardImg.src)) || '';
    img.alt = card.dataset.productTitle || '';

    sheet.querySelector('.pch-sheet__title').textContent = card.dataset.productTitle || '';
    sheet.querySelector('.pch-sheet__price').textContent = (
      card.querySelector('product-price .price')?.textContent || ''
    ).trim();

    const link = sheet.querySelector('.pch-sheet__link');
    if (card.dataset.productUrl) {
      link.href = card.dataset.productUrl;
      link.removeAttribute('hidden');
    } else {
      link.setAttribute('hidden', '');
    }

    // Reconstruir los grupos de opciones desde el overlay del card (mismo
    // estado: disponibles, deshabilitados y preseleccionados).
    const optionsEl = sheet.querySelector('.pch-sheet__options');
    optionsEl.innerHTML = '';
    hover.querySelectorAll('.pch-size-group, .pch-color-group').forEach((group) => {
      const wrap = document.createElement('div');
      wrap.className = 'pch-sheet__group';
      const label = document.createElement('p');
      label.className = 'pch-sheet__group-label';
      label.textContent = group.getAttribute('aria-label') || '';
      const btns = document.createElement('div');
      btns.className = 'pch-sheet__group-btns';

      group.querySelectorAll('.pch-size-btn, .pch-color-btn').forEach((orig) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'pch-sheet__opt' + (orig.classList.contains('pch-color-btn') ? ' pch-sheet__opt--swatch' : '');
        b.dataset.sheetOption = '';
        b.dataset.optionName = orig.dataset.optionName;
        b.dataset.optionValue = orig.dataset.optionValue;
        b.disabled = orig.disabled || orig.getAttribute('aria-disabled') === 'true';
        b.innerHTML = orig.innerHTML;
        if (orig.classList.contains('pch-selected')) b.classList.add('pch-selected');
        btns.appendChild(b);
      });

      wrap.appendChild(label);
      wrap.appendChild(btns);
      optionsEl.appendChild(wrap);
    });

    // El CTA hereda los colores del boton Comprar ahora del card (settings).
    const cardBuyBtn = card.querySelector('.pch-buy-bar__btn');
    const cta = sheet.querySelector('.pch-sheet__cta');
    if (cardBuyBtn) {
      const cs = getComputedStyle(cardBuyBtn);
      cta.style.background = cs.backgroundColor;
      cta.style.color = cs.color;
    }
    cta.disabled = false;

    sheet.querySelector('.pch-sheet__error').setAttribute('hidden', '');
    sheet.hidden = false;
    document.documentElement.classList.add('pch-sheet-open');
    sheet.querySelector('.pch-sheet__close').focus({ preventScroll: true });
  }

  function closeSheet() {
    if (!sheetEl) return;
    sheetEl.hidden = true;
    sheetState = null;
    document.documentElement.classList.remove('pch-sheet-open');
  }

  document.addEventListener('click', (e) => {
    const buyBtn = e.target.closest('[data-product-card-buy]');
    const addBtn = e.target.closest('[data-product-card-add]');
    const btn = buyBtn || addBtn;
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const productCard = btn.closest('product-card');
    if (!productCard) return;

    const hover = productCard.querySelector('product-card-hover');
    if (hover instanceof ProductCardHoverOverlay) {
      if (buyBtn) {
        // Mobile/touch: si falta elegir talla/color, abrir el preview en
        // bottom-sheet (no hay hover para el overlay). Con una sola variante
        // ya preseleccionada, compra directo sin modal.
        if (isTouch() && hover._getMissingOptions().length > 0) {
          openSheet(hover);
        } else {
          hover.handleBuyNow(btn);
        }
      } else {
        hover.handleCartAdd(btn);
      }
    }
  });
})();
